import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import type { Translate } from '~/shared/internationalization/translation';
import { Badge } from '~/ui/core/badge/Badge';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';

type OpenExternalLink = (href: string) => void;

const ExternalLinkConfirmationContext = createContext<OpenExternalLink | undefined>(undefined);

export const getExternalHttpUrl = (href: string, currentUrl: string): URL | null => {
    if (!URL.canParse(href, currentUrl)) return null;

    const url = new URL(href, currentUrl);

    return ['http:', 'https:'].includes(url.protocol) && url.origin !== new URL(currentUrl).origin ? url : null;
};

export const useOpenExternalLink = (): OpenExternalLink => {
    const openExternalLink = useContext(ExternalLinkConfirmationContext);

    if (openExternalLink === undefined) {
        throw new Error('useOpenExternalLink must be used inside ExternalLinkConfirmation');
    }

    return openExternalLink;
};

export const ExternalLinkConfirmation: FunctionComponent<PropsWithChildren<{ readonly translate: Translate }>> = ({
    children,
    translate,
}) => {
    const [externalUrl, setExternalUrl] = useState<URL | null>(null);
    const openExternalLink = useCallback((href: string): void => {
        const url = getExternalHttpUrl(href, globalThis.location.href);

        if (url === null) {
            globalThis.open(href, '_blank', 'noopener,noreferrer');
            return;
        }

        setExternalUrl(url);
    }, []);
    useEffect(() => {
        const handleClick = (event: MouseEvent): void => {
            if (event.defaultPrevented || event.button !== 0 || !(event.target instanceof Element)) return;

            const link = event.target.closest('a[href]');
            if (!(link instanceof HTMLAnchorElement) || link.hasAttribute('download')) return;

            const url = getExternalHttpUrl(link.href, globalThis.location.href);
            if (url === null) return;

            event.preventDefault();
            setExternalUrl(url);
        };

        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    return (
        <ExternalLinkConfirmationContext.Provider value={openExternalLink}>
            {children}
            <ConfirmationDialog
                cancelButtonLabel={translate('common.cancel')}
                confirmButtonLabel={translate('common.continue')}
                confirmButtonVariant="default"
                description={
                    <Trans
                        components={{
                            host: (
                                <Badge size="m" variant="gray">
                                    {null}
                                </Badge>
                            ),
                        }}
                        i18nKey="common.external-link.description"
                        values={{ host: externalUrl?.host ?? '' }}
                    />
                }
                open={externalUrl !== null}
                title={translate('common.external-link.title')}
                onCancel={() => setExternalUrl(null)}
                onConfirm={() => {
                    if (externalUrl !== null) globalThis.open(externalUrl.href, '_blank', 'noopener,noreferrer');
                    setExternalUrl(null);
                }}
            />
        </ExternalLinkConfirmationContext.Provider>
    );
};
